import React from "react";
import { Lock, Megaphone, ListOrdered, Loader2, AlertTriangle, Inbox } from "lucide-react";

export const Loading = ({ label = "Loading…" }: { label?: string }) => (
  <div role="status" aria-live="polite" className="flex items-center gap-2 py-6 text-sm text-[var(--muted)]" data-testid="loading-state">
    <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {label}
  </div>
);

export const ErrorBox = ({ message, onRetry }: { message: string; onRetry?: () => void }) => (
  <div role="alert" className="flex flex-wrap items-center gap-3 rounded border border-[var(--red)] bg-[var(--red-soft)] p-3 text-sm text-[var(--red)]" data-testid="error-state">
    <AlertTriangle className="h-4 w-4" aria-hidden /> <span>{message}</span>
    {onRetry && <button className="btn btn-sm btn-danger" onClick={onRetry} data-testid="error-retry-button">Retry</button>}
  </div>
);

export const Empty = ({ title, children }: { title: string; children?: React.ReactNode }) => (
  <div className="rounded border border-dashed border-[var(--steel)] bg-white p-6 text-center" data-testid="empty-state">
    <Inbox className="mx-auto mb-2 h-6 w-6 text-[var(--muted)]" aria-hidden />
    <p className="font-semibold">{title}</p>
    {children && <div className="mt-1 text-sm text-[var(--muted)]">{children}</div>}
  </div>
);

export const ProtectedTag = ({ text = "Protected — shared only with your consent" }: { text?: string }) => (
  <span className="badge badge-red" data-testid="protected-tag"><Lock className="h-3 w-3" aria-hidden />{text}</span>
);

export const SponsoredTag = () => (
  <span className="badge badge-amber" data-testid="sponsored-tag"><Megaphone className="h-3 w-3" aria-hidden />Sponsored</span>
);

export const OrganicTag = ({ text = "Organic results" }: { text?: string }) => (
  <span className="badge badge-blue" data-testid="organic-tag"><ListOrdered className="h-3 w-3" aria-hidden />{text}</span>
);

const STATUS_TONE: Record<string, string> = {
  approved: "badge-green", accepted: "badge-green", granted: "badge-green", open: "badge-blue", test_active: "badge-green",
  pending: "badge-amber", submitted: "badge-amber", limited: "badge-amber", test_paused: "badge-amber",
  declined: "badge-red", rejected: "badge-red", revoked: "badge-red", suspended: "badge-red", expired: "badge-red",
  superseded: "badge-red", cancelled: "badge-red", unavailable: "badge-red", test_cancelled: "badge-red", available: "badge-green",
  deny: "badge-red", allow: "badge-green",
};

export const StatusBadge = ({ value, testId }: { value: string; testId?: string }) => (
  <span className={`badge ${STATUS_TONE[value] || ""}`} data-testid={testId}>{(value || "").replace(/_/g, " ")}</span>
);

export const Field = ({ label, id, hint, children }: { label: string; id: string; hint?: string; children: React.ReactNode }) => (
  <div>
    <label className="label" htmlFor={id}>{label}</label>
    {children}
    {hint && <p className="hint" id={`${id}-hint`}>{hint}</p>}
  </div>
);

export const PageTitle = ({ kicker, title, children }: { kicker?: string; title: string; children?: React.ReactNode }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4 fade-up">
    <div>
      {kicker && <p className="mono text-xs uppercase tracking-widest text-[var(--red)]">{kicker}</p>}
      <h1 className="text-4xl font-extrabold sm:text-5xl">{title}</h1>
    </div>
    {children}
  </div>
);

export function DashLayout({ title, tabs, value, onChange, idPrefix, action, children }: { title: string; tabs: [string, string, any?][]; value: string; onChange: (v: string) => void; idPrefix: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)] gap-6 px-4 py-8 lg:grid-cols-[220px_minmax(0,1fr)]">
      <nav aria-label={`${title} sections`} className="h-fit min-w-0 rounded-lg border border-[var(--steel)] bg-white p-2" role="tablist">
        <ul className="flex gap-1 overflow-x-auto lg:block lg:space-y-1">
          {tabs.map(([key, label, Icon]) => (
            <li key={key}>
              <button role="tab" aria-selected={value === key} className={`side-item ${value === key ? "side-item-active" : ""}`} onClick={() => onChange(key)} data-testid={`${idPrefix}-tab-${key}`}>
                {Icon && <Icon className="h-4 w-4" aria-hidden />}{label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <section className="min-w-0">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-extrabold text-[var(--navy)] sm:text-3xl">{title}</h1>
          {action}
        </div>
        {children}
      </section>
    </div>
  );
}

export function Tabs({ tabs, value, onChange, idPrefix }: { tabs: [string, string][]; value: string; onChange: (v: string) => void; idPrefix: string }) {
  return (
    <div className="tabbar mb-6" role="tablist">
      {tabs.map(([key, label]) => (
        <button key={key} role="tab" aria-selected={value === key} className="tab" onClick={() => onChange(key)} data-testid={`${idPrefix}-tab-${key}`}>
          {label}
        </button>
      ))}
    </div>
  );
}
